import { API_BASE_URL, TOKEN_STORAGE_KEY } from '../config/index.js';

/**
 * Thin fetch wrapper around the backend API.
 *
 * - Attaches the stored Bearer token automatically.
 * - Unwraps the backend's `{ data }` envelope on success.
 * - Throws an Error carrying { status, details } on failure, so callers can
 *   `try/catch` and show err.message.
 */
class ApiService {
  async request(endpoint, { method = 'GET', body, headers } = {}) {
    const token = localStorage.getItem(TOKEN_STORAGE_KEY);

    const res = await fetch(`${API_BASE_URL}${endpoint}`, {
      method,
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...headers,
      },
      ...(body ? { body: JSON.stringify(body) } : {}),
    });

    const isJson = res.headers.get('content-type')?.includes('application/json');
    const payload = isJson ? await res.json() : null;

    if (!res.ok) {
      const message = payload?.error?.message || `Request failed (${res.status})`;
      const error = new Error(message);
      error.status = res.status;
      error.details = payload?.error?.details;
      throw error;
    }

    return payload?.data ?? payload;
  }

  // ---- Auth ----
  register({ email, name, password }) {
    return this.request('/auth/register', { method: 'POST', body: { email, name, password } });
  }

  login({ email, password }) {
    return this.request('/auth/login', { method: 'POST', body: { email, password } });
  }

  me() {
    return this.request('/auth/me');
  }

  // ---- Users ----
  listUsers({ limit = 50, offset = 0 } = {}) {
    return this.request(`/users?limit=${limit}&offset=${offset}`);
  }

  // ---- RBAC console (UC f5210ad0 — User Access & RBAC Console) ----
  rbacOptions() {
    return this.request('/rbac/options');
  }

  async rbacListUsers({ page = 1, pageSize = 25, role, status, search } = {}) {
    const params = new URLSearchParams({ page, pageSize });
    if (role) params.set('role', role);
    if (status) params.set('status', status);
    if (search) params.set('search', search);
    const payload = await this._rawRequest(`/rbac/users?${params.toString()}`);
    return payload;
  }

  rbacInviteUser({ email, role, landingPageSlug }) {
    return this.request('/rbac/users/invite', {
      method: 'POST',
      body: { email, role, ...(landingPageSlug ? { landingPageSlug } : {}) },
    });
  }

  rbacUpdateUserAccess(id, patch) {
    return this.request(`/rbac/users/${id}`, { method: 'PATCH', body: patch });
  }

  async rbacListAuditLogs({ page = 1, pageSize = 25 } = {}) {
    const params = new URLSearchParams({ page, pageSize });
    return this._rawRequest(`/rbac/audit-logs?${params.toString()}`);
  }

  // ---- Tenant Settings & Rate Cards (UC 8fdad578 — Configure Tenant Rate Cards and System Policies) ----
  getTenantSettings() {
    return this.request('/tenant-settings');
  }

  saveTenantSettings(patch) {
    return this.request('/tenant-settings', { method: 'PUT', body: patch });
  }

  // ---- Internal Crono Reminders (UC 535f1e60 — Configure Scheduled Crono Reminders for Internal Proof Collection) ----
  listCronoReminderRules() {
    return this.request('/tenant-settings/crono-reminder-rules');
  }

  createCronoReminderRule(body) {
    return this.request('/tenant-settings/crono-reminder-rules', { method: 'POST', body });
  }

  updateCronoReminderRule(id, patch) {
    return this.request(`/tenant-settings/crono-reminder-rules/${id}`, { method: 'PUT', body: patch });
  }

  deleteCronoReminderRule(id) {
    return this.request(`/tenant-settings/crono-reminder-rules/${id}`, { method: 'DELETE' });
  }

  async listCronoReminderExecutions({ page = 1, pageSize = 10 } = {}) {
    const params = new URLSearchParams({ page, pageSize });
    return this._rawRequest(`/tenant-settings/crono-reminder-executions?${params.toString()}`);
  }

  // ---- Proposal Template Library (UC 9304372e — Manage Proposal Templates and Merged Scope Blocks) ----
  async listProposalTemplates({ page = 1, pageSize = 25, pricingModel, status, search } = {}) {
    const params = new URLSearchParams({ page, pageSize });
    if (pricingModel) params.set('pricingModel', pricingModel);
    if (status) params.set('status', status);
    if (search) params.set('search', search);
    return this._rawRequest(`/proposal-templates?${params.toString()}`);
  }

  getProposalTemplate(id) {
    return this.request(`/proposal-templates/${id}`);
  }

  createProposalTemplate(body) {
    return this.request('/proposal-templates', { method: 'POST', body });
  }

  updateProposalTemplate(id, patch) {
    return this.request(`/proposal-templates/${id}`, { method: 'PUT', body: patch });
  }

  // "Upload DOCX Template" (UC da66e2a8 — Upload and Parse Word (.docx) Proposal and Contract Templates)
  uploadProposalTemplateFile(formData) {
    return this._formRequest('/proposal-templates/upload', formData);
  }

  confirmProposalTemplateUpload(body) {
    return this.request('/proposal-templates/upload/confirm', { method: 'POST', body });
  }

  // ---- Integration Connectors Hub (UC 82cfe8d7 — Configure and Authorize GHL CRM Connector) ----
  listIntegrationConnectors() {
    return this.request('/integration-connectors');
  }

  connectIntegrationConnector(id, { locationId, apiKey }) {
    return this.request(`/integration-connectors/${id}/connect`, {
      method: 'POST',
      body: { locationId, apiKey },
    });
  }

  pingIntegrationConnector(id) {
    return this.request(`/integration-connectors/${id}/ping`, { method: 'POST' });
  }

  async listIntegrationSyncLogs({ page = 1, pageSize = 10, connectorId, failedOnly } = {}) {
    const params = new URLSearchParams({ page, pageSize });
    if (connectorId) params.set('connectorId', connectorId);
    if (failedOnly) params.set('failedOnly', 'true');
    return this._rawRequest(`/integration-connectors/sync-logs?${params.toString()}`);
  }

  getIntegrationSyncLog(logId) {
    return this.request(`/integration-connectors/sync-logs/${logId}`);
  }

  replayIntegrationSyncLog(logId) {
    return this.request(`/integration-connectors/sync-logs/${logId}/replay`, { method: 'POST' });
  }

  // ---- GHL CRM Commercial Context ingestion (UC f378da6c — Ingest GHL CRM Commercial Context for Proposals) ----
  async listCrmDeals({ stage } = {}) {
    const params = new URLSearchParams();
    if (stage) params.set('stage', stage);
    const qs = params.toString();
    return this.request(`/crm-deals${qs ? `?${qs}` : ''}`);
  }

  importCrmDeal(ghlDealId) {
    return this.request('/crm-deals/import', { method: 'POST', body: { ghlDealId } });
  }

  // ---- Margin floor override requests (UC f662952c — Generate Governed Commercial Proposal) ----
  async listMarginOverrideRequests({ draftCode, status, page = 1, pageSize = 25 } = {}) {
    const params = new URLSearchParams({ page, pageSize });
    if (draftCode) params.set('draftCode', draftCode);
    if (status) params.set('status', status);
    return this._rawRequest(`/margin-overrides?${params.toString()}`);
  }

  requestMarginOverride(body) {
    return this.request('/margin-overrides', { method: 'POST', body });
  }

  // ---- Margin override approvals queue (UC 46f9ec79 — Approve Proposal Scope and Discount Overrides) ----
  async listMarginOverridesQueue({ draftCode, status, page = 1, pageSize = 25 } = {}) {
    const params = new URLSearchParams({ page, pageSize });
    if (draftCode) params.set('draftCode', draftCode);
    if (status) params.set('status', status);
    return this._rawRequest(`/margin-overrides/queue?${params.toString()}`);
  }

  decideMarginOverride(id, body) {
    return this.request(`/margin-overrides/${id}/decide`, { method: 'POST', body });
  }

  // ---- Proposal milestone payment schedule (UC fb0691aa — Configure Proposal Milestone Schedule and Payment Terms) ----
  async listProposalMilestones({ draftCode }) {
    const params = new URLSearchParams({ draftCode });
    return this._rawRequest(`/proposal-milestones?${params.toString()}`);
  }

  createProposalMilestone(draftCode, body) {
    const params = new URLSearchParams({ draftCode });
    return this.request(`/proposal-milestones?${params.toString()}`, { method: 'POST', body });
  }

  updateProposalMilestone(draftCode, id, body) {
    const params = new URLSearchParams({ draftCode });
    return this.request(`/proposal-milestones/${id}?${params.toString()}`, { method: 'PUT', body });
  }

  async deleteProposalMilestone(draftCode, id) {
    const params = new URLSearchParams({ draftCode });
    const token = localStorage.getItem(TOKEN_STORAGE_KEY);
    const res = await fetch(`${API_BASE_URL}/proposal-milestones/${id}?${params.toString()}`, {
      method: 'DELETE',
      headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    });
    if (!res.ok) {
      const isJson = res.headers.get('content-type')?.includes('application/json');
      const payload = isJson ? await res.json() : null;
      const message = payload?.error?.message || `Request failed (${res.status})`;
      const error = new Error(message);
      error.status = res.status;
      throw error;
    }
  }

  // ---- Proposal commercial signoff approvals (UC cd03778e — Review and Approve Multipage Proposal Document) ----
  async listProposalApprovals({ draftCode, status, page = 1, pageSize = 25 } = {}) {
    const params = new URLSearchParams({ page, pageSize });
    if (draftCode) params.set('draftCode', draftCode);
    if (status) params.set('status', status);
    return this._rawRequest(`/proposal-approvals?${params.toString()}`);
  }

  getLatestProposalApproval(draftCode) {
    const params = new URLSearchParams({ draftCode });
    return this.request(`/proposal-approvals/latest?${params.toString()}`);
  }

  issueProposalApproval(body) {
    return this.request('/proposal-approvals', { method: 'POST', body });
  }

  // ---- Document & E-Sign Hub (UC 467740a4 — Convert Proposal to PDF and Dispatch via PandaDoc or DocuSign) ----
  async listDocuments({ search, provider, status, fileType, page = 1, pageSize = 25 } = {}) {
    const params = new URLSearchParams({ page, pageSize });
    if (search) params.set('search', search);
    if (provider) params.set('provider', provider);
    if (status) params.set('status', status);
    if (fileType) params.set('fileType', fileType);
    return this._rawRequest(`/documents?${params.toString()}`);
  }

  getDocument(id) {
    return this.request(`/documents/${id}`);
  }

  /** "Convert to PDF" — Proposal Studio's action that renders + persists the document. */
  convertProposalToPdf(body) {
    return this.request('/documents/convert', { method: 'POST', body });
  }

  /** E-Sign Dispatch Modal's "Dispatch Request" action. */
  dispatchDocumentForSignature(id, body) {
    return this.request(`/documents/${id}/dispatch`, { method: 'POST', body });
  }

  resendDocumentSignatureRequest(id) {
    return this.request(`/documents/${id}/resend`, { method: 'POST' });
  }

  // ---- Track E-Signature Status and Archival (UC d03ff90f) ----
  /** "Refresh E-Sign Status" header button — advances every in-flight document one lifecycle step. */
  syncDocumentEsignStatus() {
    return this.request('/documents/sync-esign-status', { method: 'POST' });
  }

  /** "View Audit Trail" row action — the provider certificate of completion + timestamp log. */
  getDocumentAuditTrail(id) {
    return this.request(`/documents/${id}/esign-events`);
  }

  /** "Transfer to Contract Skeleton" row action — real, persisted handoff into the Contract Term Repository. */
  transferDocumentToContract(id) {
    return this.request(`/documents/${id}/transfer-to-contract`, { method: 'POST' });
  }

  /** Downloads the generated PDF as a Blob (auth header attached, unlike a plain <a href>). */
  async downloadDocumentContent(id) {
    const token = localStorage.getItem(TOKEN_STORAGE_KEY);
    const res = await fetch(`${API_BASE_URL}/documents/${id}/content`, {
      headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    });
    if (!res.ok) {
      const isJson = res.headers.get('content-type')?.includes('application/json');
      const payload = isJson ? await res.json() : null;
      const message = payload?.error?.message || `Request failed (${res.status})`;
      const error = new Error(message);
      error.status = res.status;
      throw error;
    }
    const disposition = res.headers.get('content-disposition') || '';
    const match = disposition.match(/filename="?([^"]+)"?/);
    return { blob: await res.blob(), filename: match ? match[1] : `document-${id}.pdf` };
  }

  // ---- Contract Template Library (UC d72ae263 — Manage Contract Templates and Clause Library) ----
  async listContractTemplates({ page = 1, pageSize = 25, category, search } = {}) {
    const params = new URLSearchParams({ page, pageSize });
    if (category) params.set('category', category);
    if (search) params.set('search', search);
    return this._rawRequest(`/contract-templates?${params.toString()}`);
  }

  getContractTemplate(id) {
    return this.request(`/contract-templates/${id}`);
  }

  createContractTemplate(body) {
    return this.request('/contract-templates', { method: 'POST', body });
  }

  updateContractTemplate(id, patch) {
    return this.request(`/contract-templates/${id}`, { method: 'PUT', body: patch });
  }

  cloneContractTemplate(id) {
    return this.request(`/contract-templates/${id}/clone`, { method: 'POST' });
  }

  /** "Upload Contract (.docx / .pdf)" step 1 — multipart upload, returns { upload, parsed } (not yet persisted). */
  uploadContractTemplateFile(formData) {
    return this._formRequest('/contract-templates/upload', formData);
  }

  /** "Convert to Active Template" — commits the parsed upload preview as a persisted template. */
  confirmContractTemplateUpload(body) {
    return this.request('/contract-templates/upload/confirm', { method: 'POST', body });
  }

  // ---- Contract Term Repository (UC 557a3bcc — Carry Proposal Terms into Contract Skeleton) ----
  async listContracts({ page = 1, pageSize = 25, status, paymentTerms, search } = {}) {
    const params = new URLSearchParams({ page, pageSize });
    if (status) params.set('status', status);
    if (paymentTerms) params.set('paymentTerms', paymentTerms);
    if (search) params.set('search', search);
    return this._rawRequest(`/contracts?${params.toString()}`);
  }

  getContract(id) {
    return this.request(`/contracts/${id}`);
  }

  /** Proposal Studio's "Transfer to Contract" action — locks the approved proposal's terms + milestone schedule into a new Contract Skeleton. */
  transferProposalToContract(body) {
    return this.request('/contracts/transfer', { method: 'POST', body });
  }

  /**
   * External Contract Import step 1 — multipart upload of a contract created
   * outside MarginOS. Returns { upload, parsed }; parsed values are
   * confidence-tagged SUGGESTIONS for the operator to review, and nothing is
   * persisted until confirmContractImport.
   */
  importContractFile(formData) {
    return this._formRequest('/contracts/import', formData);
  }

  /** External Contract Import step 2 — commits the reviewed terms as a real contract. */
  confirmContractImport(body) {
    return this.request('/contracts/import/confirm', { method: 'POST', body });
  }

  /** Activates a fully executed contract (Draft Skeleton -> Active). Serves both origins. */
  activateContract(id) {
    return this.request(`/contracts/${id}/activate`, { method: 'POST' });
  }

  // ---- Cash application (Collections) ----

  /** Receipts recorded against one invoice, with its settled balance. */
  listInvoicePayments(invoiceId) {
    return this._rawRequest(`/collections/invoices/${invoiceId}/payments`);
  }

  /** Records a payment learned outside the accounting ledger. A negative amount is a refund. */
  recordInvoicePayment(invoiceId, body) {
    return this.request(`/collections/invoices/${invoiceId}/payments`, { method: 'POST', body });
  }

  /** Pulls receipts back from the accounting ledger and settles invoices. Idempotent. */
  syncLedgerPayments() {
    return this.request('/collections/payments/sync-ledger', { method: 'POST' });
  }

  /** Cash collected, outstanding receivable, and settlement latency for a window. */
  getCollectionsPerformance({ fromDate, toDate } = {}) {
    const params = new URLSearchParams();
    if (fromDate) params.set('fromDate', fromDate);
    if (toDate) params.set('toDate', toDate);
    const qs = params.toString();
    return this.request(`/collections/performance${qs ? `?${qs}` : ''}`);
  }

  // ---- Renewal radar (Contracts) ----

  listRenewalQueue({ page = 1, pageSize = 25, windowDays = 90 } = {}) {
    const params = new URLSearchParams({ page, pageSize, windowDays });
    return this._rawRequest(`/contracts/renewal-queue?${params.toString()}`);
  }

  /** Re-derives every live contract's renewal state from its end date. Idempotent. */
  sweepRenewalStates(windowDays = 90) {
    return this.request('/contracts/renewal-sweep', { method: 'POST', body: { windowDays } });
  }

  /** Records the outcome of a case-study ask (requested, captured, or declined). */
  updateContractCaseStudy(id, body) {
    return this.request(`/contracts/${id}/case-study`, { method: 'POST', body });
  }

  // ---- Revenue attribution (CRM) ----

  /** Realised revenue by campaign and lead source — contracted, invoiced, collected. */
  getRevenueAttribution({ fromDate, toDate } = {}) {
    const params = new URLSearchParams();
    if (fromDate) params.set('fromDate', fromDate);
    if (toDate) params.set('toDate', toDate);
    const qs = params.toString();
    return this._rawRequest(`/crm-deals/revenue-attribution${qs ? `?${qs}` : ''}`);
  }

  /** CRM providers whose connector is currently authorized. */
  listCrmProviders() {
    return this.request('/crm-deals/providers');
  }

  // ---- UAE e-invoicing ----

  /** Seller identity, ASP appointment, invoice readiness, and days to each mandate milestone. */
  getEinvoicingCompliance() {
    return this.request('/einvoicing/compliance');
  }

  /** Per-invoice PINT AE readiness — which would pass ASP validation and why not. */
  getEinvoicingReadiness({ page = 1, pageSize = 25, status } = {}) {
    const params = new URLSearchParams({ page, pageSize });
    if (status) params.set('status', status);
    return this._rawRequest(`/einvoicing/readiness?${params.toString()}`);
  }

  dispatchEinvoice(invoiceId) {
    return this.request(`/einvoicing/invoices/${invoiceId}/dispatch`, { method: 'POST' });
  }

  updateEinvoicingSellerIdentity(body) {
    return this.request('/einvoicing/seller-identity', { method: 'PUT', body });
  }

  updateEinvoicingBuyerIdentity(accountName, body) {
    return this.request(`/einvoicing/buyer-identity/${encodeURIComponent(accountName)}`, { method: 'PUT', body });
  }

  // ---- Scheduler ----

  listScheduledJobs() {
    return this.request('/scheduler/jobs');
  }

  updateScheduledJob(jobKey, body) {
    return this.request(`/scheduler/jobs/${jobKey}`, { method: 'PUT', body });
  }

  runScheduledJob(jobKey) {
    return this.request(`/scheduler/jobs/${jobKey}/run`, { method: 'POST' });
  }

  listScheduledJobRuns({ page = 1, pageSize = 25, jobKey } = {}) {
    const params = new URLSearchParams({ page, pageSize });
    if (jobKey) params.set('jobKey', jobKey);
    return this._rawRequest(`/scheduler/runs?${params.toString()}`);
  }

  /** "Dispatch for E-Signature" — Editable Multipage Contract Document Preview modal's Signer Verification Panel (UC d2ad06ec). */
  dispatchContractEsign(id, body) {
    return this.request(`/contracts/${id}/dispatch-esign`, { method: 'POST', body });
  }

  /** Downloads the rendered Contract Skeleton PDF as a Blob (auth header attached, unlike a plain <a href>). */
  async downloadContractSkeletonPdf(id) {
    const token = localStorage.getItem(TOKEN_STORAGE_KEY);
    const res = await fetch(`${API_BASE_URL}/contracts/${id}/skeleton-pdf`, {
      headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    });
    if (!res.ok) {
      const isJson = res.headers.get('content-type')?.includes('application/json');
      const payload = isJson ? await res.json() : null;
      const message = payload?.error?.message || `Request failed (${res.status})`;
      const error = new Error(message);
      error.status = res.status;
      throw error;
    }
    const disposition = res.headers.get('content-disposition') || '';
    const match = disposition.match(/filename="?([^"]+)"?/);
    return { blob: await res.blob(), filename: match ? match[1] : `contract-${id}.pdf` };
  }

  // ---- Active Project Delivery Hub / Milestone Billing Proof Workspace (UC 1021f3de) ----
  async listActiveEngagements({ page = 1, pageSize = 15, status, billingMethod, search } = {}) {
    const params = new URLSearchParams({ page, pageSize });
    if (status) params.set('status', status);
    if (billingMethod) params.set('billingMethod', billingMethod);
    if (search) params.set('search', search);
    return this._rawRequest(`/project-delivery/engagements?${params.toString()}`);
  }

  getEngagementMilestones(contractId) {
    return this.request(`/project-delivery/engagements/${contractId}/milestones`);
  }

  async listMilestoneProofs({ contractId, page = 1, pageSize = 25 } = {}) {
    const params = new URLSearchParams({ page, pageSize });
    if (contractId) params.set('contractId', contractId);
    return this._rawRequest(`/project-delivery/proofs?${params.toString()}`);
  }

  /** "Submit Proof & Flag Billing Ready" — multipart upload (evidence file + form fields). */
  submitMilestoneProof(formData) {
    return this._formRequest('/project-delivery/proofs', formData);
  }

  /** "Preview File" row action — auth header attached, unlike a plain <a href>. */
  async previewMilestoneProofContent(id) {
    const token = localStorage.getItem(TOKEN_STORAGE_KEY);
    const res = await fetch(`${API_BASE_URL}/project-delivery/proofs/${id}/content`, {
      headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    });
    if (!res.ok) {
      const isJson = res.headers.get('content-type')?.includes('application/json');
      const payload = isJson ? await res.json() : null;
      const message = payload?.error?.message || `Request failed (${res.status})`;
      const error = new Error(message);
      error.status = res.status;
      throw error;
    }
    const blob = await res.blob();
    return { blob, url: URL.createObjectURL(blob) };
  }

  // ---- T&M Evidence & Expense Ledger (UC 2a654939) ----
  async listTmLedgerEntries({ contractId, approvalState, page = 1, pageSize = 25 } = {}) {
    const params = new URLSearchParams({ page, pageSize });
    if (contractId) params.set('contractId', contractId);
    if (approvalState) params.set('approvalState', approvalState);
    return this._rawRequest(`/project-delivery/tm-entries?${params.toString()}`);
  }

  /** "Log Billable Item to Ledger" — multipart (evidence file optional + form fields). */
  logTmLedgerEntry(formData) {
    return this._formRequest('/project-delivery/tm-entries', formData);
  }

  deleteTmLedgerEntry(id) {
    return this.request(`/project-delivery/tm-entries/${id}`, { method: 'DELETE' });
  }

  /** "Approve Batch for Invoice" — transitions the engagement's Draft batch to Lead Approved. */
  approveTmLedgerBatch(contractId) {
    return this.request('/project-delivery/tm-entries/approve-batch', { method: 'POST', body: { contractId } });
  }

  // ---- Review Billing Readiness and Assemble Invoice Source Items (UC 3f835112) ----

  /** Billing Readiness Review — proof-verified milestones + approved T&M batches, selectable for invoice assembly. */
  getBillingReadiness() {
    return this.request('/project-delivery/billing-readiness');
  }

  /** "Attach Exception Waiver" — multipart (signed waiver document + milestoneId/contractId + notes). */
  attachBillingWaiver(formData) {
    return this._formRequest('/project-delivery/billing-waivers', formData);
  }

  /** "Assemble Invoice Source Package" — bundle selected proof-verified/waived items for one engagement. */
  assembleInvoiceSourcePackage(body) {
    return this.request('/project-delivery/invoice-source-packages', { method: 'POST', body });
  }

  async listInvoiceSourcePackages({ contractId, status, page = 1, pageSize = 25 } = {}) {
    const params = new URLSearchParams({ page, pageSize });
    if (contractId) params.set('contractId', contractId);
    if (status) params.set('status', status);
    return this._rawRequest(`/project-delivery/invoice-source-packages?${params.toString()}`);
  }

  // ---- Process Milestone Invoice and Sync Ledger (UC 1331d84b) ----

  async listMilestoneInvoices({ contractId, page = 1, pageSize = 25 } = {}) {
    const params = new URLSearchParams({ page, pageSize });
    if (contractId) params.set('contractId', contractId);
    return this._rawRequest(`/project-delivery/invoices?${params.toString()}`);
  }

  /** "Generate & Sync Invoice" — issues a real invoice from an assembled invoice source package and posts it to the ledger. */
  generateMilestoneInvoice(packageId) {
    return this.request('/project-delivery/invoices', { method: 'POST', body: { packageId } });
  }

  /** Retry ledger sync for one already-generated invoice. */
  syncMilestoneInvoice(invoiceId) {
    return this.request(`/project-delivery/invoices/${invoiceId}/sync`, { method: 'POST' });
  }

  /** "Sync All to Xero ERP" — batch-retry every invoice not yet successfully synced. */
  syncAllMilestoneInvoices() {
    return this.request('/project-delivery/invoices/sync-all', { method: 'POST' });
  }

  // ---- Collections & Dunning Command Center (UC cf1e7912 — Sync GHL Billing Contacts for Invoice and Dunning Workflows) ----

  getCollectionsAgingSummary() {
    return this.request('/collections/aging-summary');
  }

  async listDunningQueue({ page = 1, pageSize = 25, search } = {}) {
    const params = new URLSearchParams({ page, pageSize });
    if (search) params.set('search', search);
    return this._rawRequest(`/collections/dunning-queue?${params.toString()}`);
  }

  listGhlBillingContacts() {
    return this.request('/collections/billing-contacts');
  }

  syncGhlBillingContacts() {
    return this.request('/collections/billing-contacts/sync', { method: 'POST' });
  }

  /**
   * "Route GHL Email" / "Send Reminder" — dispatch a dunning/reminder notice for one
   * invoice to its synced GHL billing contact. `body` is optional — the Account
   * Manager Reminder Queue (UC 55b51537) passes `{ subject, message }` to send the
   * edited reminder context instead of the auto-generated dunning message.
   */
  dispatchDunningNotice(invoiceId, body) {
    return this.request(`/collections/invoices/${invoiceId}/dispatch-dunning`, { method: 'POST', body });
  }

  logPromiseToPay(invoiceId, body) {
    return this.request(`/collections/invoices/${invoiceId}/promise-to-pay`, { method: 'POST', body });
  }

  executeScheduledDunningRun() {
    return this.request('/collections/dunning-run', { method: 'POST' });
  }

  // ---- Engagement Lifecycle Tracker & Cashflow Hub (UC 6bc74ce1 — Track Billing and Collections State by Engagement) ----

  getEngagementLifecycleSummary() {
    return this.request('/engagement-lifecycle/summary');
  }

  getEngagementCashflowProjection(horizon = '30', riskLevel) {
    const params = new URLSearchParams({ horizon });
    if (riskLevel) params.set('riskLevel', riskLevel);
    return this.request(`/engagement-lifecycle/cashflow-projection?${params.toString()}`);
  }

  getEngagementStageMix() {
    return this.request('/engagement-lifecycle/stage-mix');
  }

  async listEngagementLifecycleMatrix({ page = 1, pageSize = 25, search, stage, riskLevel, deliveryLeadId, revenueOwnerId } = {}) {
    const params = new URLSearchParams({ page, pageSize });
    if (search) params.set('search', search);
    if (stage) params.set('stage', stage);
    if (riskLevel) params.set('riskLevel', riskLevel);
    if (deliveryLeadId) params.set('deliveryLeadId', deliveryLeadId);
    if (revenueOwnerId) params.set('revenueOwnerId', revenueOwnerId);
    return this._rawRequest(`/engagement-lifecycle/engagements?${params.toString()}`);
  }

  getEngagementDeliveryLeads() {
    return this.request('/engagement-lifecycle/delivery-leads');
  }

  getEngagementLifecycleTimeline(contractId) {
    return this.request(`/engagement-lifecycle/engagements/${contractId}/timeline`);
  }

  syncEngagementCrmStatus(contractId) {
    return this.request(`/engagement-lifecycle/engagements/${contractId}/sync-crm`, { method: 'POST' });
  }

  // ---- Payer Profile & Scorecard (UC a10bb353 — Review Payer Payment Behavior and Credit Profile) ----

  getPayerAccounts() {
    return this.request('/payer-intelligence/accounts');
  }

  /** Loaded separately from the scorecard so a slow CreditIQ cannot delay the
   *  tenant's own payer data (MOS-NFR-06). */
  getPayerNetworkReputation(accountName) {
    const params = new URLSearchParams({ accountName });
    return this.request(`/payer-intelligence/network-reputation?${params.toString()}`);
  }

  /** Contribute this payer's own payment behaviour to CreditIQ (MOS-FBK-03).
   *  Always resolves with an outcome; a refusal is data, not an error. */
  contributePayerSignal(accountName) {
    // request() serialises `body` itself — passing a pre-stringified value here
    // double-encodes it and the server receives a JSON string, not an object.
    return this.request('/payer-intelligence/contribute-signal', {
      method: 'POST',
      body: { accountName },
    });
  }

  getPayerScorecard(accountName) {
    const params = new URLSearchParams({ accountName });
    return this.request(`/payer-intelligence/scorecard?${params.toString()}`);
  }

  async listPayerDisputes(accountName, { page = 1, pageSize = 10 } = {}) {
    const params = new URLSearchParams({ accountName, page, pageSize });
    return this._rawRequest(`/payer-intelligence/disputes?${params.toString()}`);
  }

  // ---- OaaS Metering & Usage Portal (UC e1efc667 — Review OaaS Consumption Statement and Billing Run) ----

  getOaasMeteringOverview() {
    return this.request('/oaas-metering/overview');
  }

  async listOaasUsageStatements({ page = 1, pageSize = 10, status, accountName } = {}) {
    const params = new URLSearchParams({ page, pageSize });
    if (status) params.set('status', status);
    if (accountName) params.set('accountName', accountName);
    return this._rawRequest(`/oaas-metering/statements?${params.toString()}`);
  }

  runOaasBillingBatch() {
    return this.request('/oaas-metering/billing-runs', { method: 'POST' });
  }

  approveOaasStatement(id) {
    return this.request(`/oaas-metering/statements/${id}/approve`, { method: 'POST' });
  }

  async downloadOaasTelemetryExport() {
    const token = localStorage.getItem(TOKEN_STORAGE_KEY);
    const res = await fetch(`${API_BASE_URL}/oaas-metering/telemetry/export`, {
      headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    });
    if (!res.ok) {
      throw new Error(`Failed to export telemetry (${res.status})`);
    }
    return res.blob();
  }

  /** Like request(), but sends a FormData body (no JSON content-type — the browser sets the multipart boundary). */
  async _formRequest(endpoint, formData) {
    const token = localStorage.getItem(TOKEN_STORAGE_KEY);
    const res = await fetch(`${API_BASE_URL}${endpoint}`, {
      method: 'POST',
      headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      body: formData,
    });
    const isJson = res.headers.get('content-type')?.includes('application/json');
    const payload = isJson ? await res.json() : null;
    if (!res.ok) {
      const message = payload?.error?.message || `Request failed (${res.status})`;
      const error = new Error(message);
      error.status = res.status;
      error.details = payload?.error?.details;
      throw error;
    }
    return payload?.data ?? payload;
  }

  /** Like request(), but returns the full { data, meta } envelope instead of unwrapping `data`. */
  async _rawRequest(endpoint, opts) {
    const token = localStorage.getItem(TOKEN_STORAGE_KEY);
    const res = await fetch(`${API_BASE_URL}${endpoint}`, {
      method: opts?.method || 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      ...(opts?.body ? { body: JSON.stringify(opts.body) } : {}),
    });
    const isJson = res.headers.get('content-type')?.includes('application/json');
    const payload = isJson ? await res.json() : null;
    if (!res.ok) {
      const message = payload?.error?.message || `Request failed (${res.status})`;
      const error = new Error(message);
      error.status = res.status;
      error.details = payload?.error?.details;
      throw error;
    }
    return payload;
  }
}

export default new ApiService();
